import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { GridColDef, GridRenderEditCellParams } from "@mui/x-data-grid";
import type { ReactElement } from "react";
import { Action } from "../../business-logic/ActionBehavior/Action";
import { ActionConfig } from "../../business-logic/ActionBehavior/ActionConfig/ActionConfig";
import { useActionTypes } from "../../requests/actionTypes";
import {
  ALPHABET,
  KeyboardShortcutItem,
  useKeyboardShortcuts,
  useUpdateKeyboardShortcuts,
} from "../../requests/keyboardShortcuts";
import { Team } from "../../requests/teams";
import { UserPermission } from "../../requests/users";
import { ACTION_CONFIGS } from "../../utils/actionMappings";
import { LARGE_COLUMN, XL_COLUMN } from "../../utils/columnSizes";
import { getTypedEntries } from "../../utils/getTypedEntries";
import { MultiSelectGridCol } from "../CrudTable/columns/MultiSelectGridCol";
import { SelectGridCol } from "../CrudTable/columns/SelectGridCol";
import { FullFeaturedCrudGridNoIdBlob } from "../CrudTable/FullFeaturedCrudGridNoIdBlob";
import { useTagOptions } from "../simple-select/simpleSelectOptions";

// This is now codified as the specified event name in the database's keyboard_shortcuts table.
export const TAG_PREVIOUS_EVENT = "TAG PREVIOUS EVENT";

type Props = {
  resetButton: ReactElement;
};

export const KeyboardShortcutGrid = ({ resetButton }: Props) => {
  // const updateKeyboardShortcuts = useUpdateKeyboardShortcuts().mutateAsync;
  const updateKeyboardShortcuts = useUpdateKeyboardShortcuts().mutate;
  const tagOptions = useTagOptions();
  const databaseActionTypes = useActionTypes();

  const getButtonPhrase = (actionConfig: ActionConfig) =>
    actionConfig.getButtonPhrase(
      databaseActionTypes,
      { name: "Home Team" } as Team,
      { name: "Away Team" } as Team
    );

  const letters = ALPHABET.split("");

  const shortcutColumn: GridColDef = {
    field: "shortcut",
    headerName: "Shortcut",
    width: LARGE_COLUMN,
    editable: true,
    type: "singleSelect",
    valueOptions: letters.map((char) => ({
      value: char,
      label: char,
    })),
    valueFormatter: (params) => {
      const value = params.value as string | undefined;
      return value ? `"${value}"` : "";
    },
    renderHeader: (params) => <strong>{params.colDef.headerName}</strong>,
    renderEditCell: (params: GridRenderEditCellParams<KeyboardShortcutItem & { id: number }>) => {
      const usedShortcuts = new Set<string>();

      params.api.getRowModels().forEach((row, rowId) => {
        if (String(rowId) === String(params.id)) {
          return;
        }

        const shortcutValue = row.shortcut as string | undefined;
        if (shortcutValue) {
          usedShortcuts.add(shortcutValue);
        }
      });

      const handleChange = (event: SelectChangeEvent<string>) => {
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: event.target.value,
        });
      };

      const currentValue = (params.value as string | undefined) ?? "";

      return (
        <Select
          autoFocus
          fullWidth
          size="small"
          displayEmpty
          value={currentValue}
          onChange={handleChange}
          renderValue={(value) => (value === "" ? "Select shortcut" : (value as string))}
          onOpen={(event) => event.stopPropagation()}
          onClose={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <MenuItem value="">
            <em>Select shortcut</em>
          </MenuItem>
          {letters.map((char) => (
            <MenuItem
              key={char}
              value={char}
              disabled={usedShortcuts.has(char)}
              onClick={(event) => event.stopPropagation()}
            >
              {char}
            </MenuItem>
          ))}
        </Select>
      );
    },
  };

  const columns: GridColDef[] = [
    shortcutColumn,
    new SelectGridCol(
      "action",
      "Action",
      XL_COLUMN,
      [
        { value: TAG_PREVIOUS_EVENT, label: TAG_PREVIOUS_EVENT },
        ...getTypedEntries(ACTION_CONFIGS)
          .map(([value, actionConfig]) => ({
            value,
            label: getButtonPhrase(actionConfig),
          }))
          .sort((a, b) => a.label?.localeCompare(b.label)),
      ],
      (params) => {
        if (params.value === null || params.value === TAG_PREVIOUS_EVENT) {
          return TAG_PREVIOUS_EVENT;
        }
        return getButtonPhrase(ACTION_CONFIGS[params.value as Action]);
      }
    ),
    // TODO: Shortcuts can only be set up with existing tags. We should allow a method of doing it with new tags.
    new MultiSelectGridCol("tags", "Tags", tagOptions ?? []),
  ];

  const getNewRow = (id: number): KeyboardShortcutItem & { id: number } => ({
    id,
    shortcut: "",
    action: TAG_PREVIOUS_EVENT,
    tags: [],
  });

  const getDeleteDialog = (keyboardShortcut: KeyboardShortcutItem) =>
    `Delete shortcut for ${keyboardShortcut?.shortcut}?`;

  return (
    <FullFeaturedCrudGridNoIdBlob
      crudRequests={{
        isStoredAsBlob: true,
        useCollection: useKeyboardShortcuts,
        putCollection: (body) =>
          updateKeyboardShortcuts(
            body.map((keyboardShortcut) => {
              return {
                ...keyboardShortcut,
                tags: keyboardShortcut.tags.map((tag) => tag),
              };
            })
          ),
      }}
      validators={{
        isValidCreateUpdate: (item, items) => {
          if (item.shortcut === "") {
            return false;
          }
          if (
            items
              .filter((_item) => item.id !== _item.id)
              .some((_item) => _item.shortcut === item.shortcut)
          ) {
            return "Shortcut already exists.";
          }
          if (
            (item.action === TAG_PREVIOUS_EVENT || item.action === null) &&
            item.tags.length === 0
          ) {
            return "Previous event shortcut must have at least one tag.";
          }

          return true;
        },
      }}
      columns={columns}
      getNewRow={getNewRow}
      getDeleteDialog={getDeleteDialog}
      itemName="Keyboard Shortcut"
      initialSortModel={[{ field: "shortcut", sort: "asc" }]}
      fieldToFocus="shortcut"
      extraGridToolbarElement={resetButton}
      minUserPermission={UserPermission.read}
    />
  );
};
